import { SparklesIcon } from '@heroicons/react/outline';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  delay,
  SectionHeading,
  Stage,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { imageSrcSet, imageUrl, roomDesign } from './assets';
import {
  useInteriorLandingT,
  usePageVars,
  useRoomVars,
  useStartInterior,
} from './kit';
import { roomPath } from './pages';
import { INTERIOR_ROOMS } from './rooms';
import type { InteriorLandingPage, RoomPageData } from './types';

/**
 * A room page's ideas: a finished design of the room and four descriptions
 * that open the designer ("Create new") with the idea filled in. Rooms like
 * it follow, for people still choosing.
 */
export default function RoomIdeas({
  page,
  room,
}: {
  page: InteriorLandingPage;
  room: RoomPageData;
}) {
  const { t } = useInteriorLandingT();
  const roomVars = useRoomVars();
  const vars = usePageVars(page);
  const start = useStartInterior(page);
  const reveal = useReveal<HTMLDivElement>(0.15);
  const design = roomDesign(room.slug);

  const current = INTERIOR_ROOMS.find((item) => item.slug === room.slug);
  const similar = INTERIOR_ROOMS.filter(
    (item) => item.group === current?.group && item.slug !== room.slug,
  ).slice(0, 8);

  return (
    <section id='ideas' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-3 sm:px-6 lg:px-8'>
        <Stage
          variant='lavender'
          halftoneAt='0% 100%'
          className='px-5 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16'
        >
          <div
            ref={reveal.ref}
            data-reveal={reveal.state}
            className={clsxm(
              styles.reveal,
              'grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-14',
            )}
          >
            <div className={clsxm(styles.rv, 'lg:col-span-5')}>
              <div className='relative overflow-hidden rounded-[22px] shadow-[0_30px_60px_-30px_rgba(22,12,90,0.55)] ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10'>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={imageUrl(design, 960)}
                  srcSet={imageSrcSet(design)}
                  sizes='(min-width: 1024px) 460px, 90vw'
                  alt={t('{{room}} design idea made with AI', vars)}
                  loading='lazy'
                  decoding='async'
                  className='aspect-square w-full object-cover'
                />
              </div>
            </div>

            <div
              className={clsxm(styles.rv, 'lg:col-span-7')}
              style={delay(120)}
            >
              <SectionHeading
                size='md'
                title={t('{{room}} ideas', vars)}
                tail={t('to start from.')}
                lead={t(
                  'Pick one to open the designer with it filled in, then add a style or your own photo and make it yours.',
                )}
              />
              <ul className='mt-8 space-y-2.5'>
                {room.ideas.map((idea) => (
                  <li key={idea}>
                    <button
                      type='button'
                      onClick={() =>
                        start({
                          surface: 'idea',
                          mode: 'generate',
                          prompt: idea,
                        })
                      }
                      className={clsxm(
                        'group flex w-full items-center gap-3.5 rounded-2xl bg-white px-4 py-3.5 text-start text-[15px] font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.06] transition duration-200 hover:-translate-y-px hover:shadow-[0_12px_28px_-16px_rgba(22,12,90,0.5)] hover:ring-primary-500/40',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-white/[0.05] dark:ring-white/10',
                        tone.ink,
                      )}
                    >
                      <span className='flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300'>
                        <SparklesIcon className='h-4 w-4' aria-hidden />
                      </span>
                      <span className='min-w-0 flex-1'>{idea}</span>
                      <ArrowGlyph className='h-4 w-4 flex-none text-[#8a8aa1] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary-600 rtl:group-hover:-translate-x-0.5' />
                    </button>
                  </li>
                ))}
              </ul>

              {similar.length ? (
                <div className='mt-9'>
                  <p className={clsxm('text-sm font-semibold', tone.quiet)}>
                    {t('Rooms like this')}
                  </p>
                  <div className='mt-3 flex flex-wrap gap-2'>
                    {similar.map((item) => (
                      <Link
                        key={item.slug}
                        href={roomPath(item.slug)}
                        className={clsxm(
                          'rounded-full bg-white/80 px-3.5 py-1.5 text-[13px] font-medium ring-1 ring-[#0f0f28]/[0.07] transition-colors hover:bg-white hover:text-primary-700 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:text-primary-300',
                          tone.body,
                        )}
                      >
                        {t('AI {{room}} Design', roomVars(item.name))}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Stage>
      </div>
    </section>
  );
}
